const DB_NAME = 'targetingDB';
const DB_VERSION = 4;
const TABLES_STORE = 'tableConfigs';
const METRICS_STORE = 'metricsConfigs';
const DATE_STORE = 'dateConfigs'
const CAMPAIGN_DRAFT_STORE = 'campaignDraftStore';

// Replace the useMemo implementations with simple functions
const isFlipKart = () => window.location.href.indexOf('/flipkart') > -1;
const isInstamart = () => window.location.href.indexOf('/instamart') > -1;
const isMyntra = () => window.location.href.indexOf('/myntra') > -1;
const isBlinkit = () => window.location.href.indexOf('/blinkit') > -1;
const isAmazonTargeting = () => window.location.href.indexOf('/amazon/targeting') > -1;
const isAMSTargeting = () => window.location.href.indexOf('/marketing-stream') > -1;
const isZepto = () => window.location.href.indexOf('/zepto') > -1;
const isBigbasket = () => window.location.href.indexOf('/bigbasket') > -1;
const isSQPTargeting = () => window.location.href.indexOf('/search-query-performance') > -1;
const isRanking = () => window.location.href.indexOf('/amazon-ranking-dashboard') > -1;

const tableConfigParam = () => {
    if (isAmazonTargeting()) return 'tableListConfig';
    if (isAMSTargeting()) return 'tableListAmsConfig';
    if (isFlipKart()) return 'flipkartTableListConfig';
    if (isMyntra()) return 'myntraTableListConfig';
    if (isInstamart()) return 'instamartTableListConfig';
    if (isBlinkit()) return 'blinkitTableListConfig';
    if (isZepto()) return 'zeptoTableListConfig';
    if (isBigbasket()) return 'zeptoTableListConfig';
    if (isSQPTargeting()) return 'tableListSqpConfig';
    if (isRanking()) return 'tableListRankingConfig';
    return ''; // Default return
};

const metricsConfigParam = () => {
    if (isAmazonTargeting()) return 'amazonMetrics';
    if (isAMSTargeting()) return 'amazonAMSMetrics';
    if (isFlipKart()) return 'flipkartMetrics';
    if (isMyntra()) return 'myntraMetrics';
    if (isInstamart()) return 'instamartMetrics';
    if (isBlinkit()) return 'blinkitMetrics';
    if (isZepto()) return 'zeptoMetrics';
    if (isBigbasket()) return 'zeptoMetrics';
    if (isSQPTargeting()) return 'amazonSQPMetrics';
    if (isRanking()) return 'amazonRankingMetrics';
    return '';
};

// Add this helper function after the constants
const verifyStoresExist = (db) => {
  const requiredStores = [TABLES_STORE, METRICS_STORE, DATE_STORE, CAMPAIGN_DRAFT_STORE];
  const missingStores = requiredStores.filter(store => !db.objectStoreNames.contains(store));
  if (missingStores.length > 0) {
    throw new Error(`Missing required stores: ${missingStores.join(', ')}`);
  }
};

// Initialize the database
const initDB = () => {
  return new Promise((resolve, reject) => {
    // First, try to get the current version
    const versionCheck = indexedDB.open(DB_NAME);
    
    versionCheck.onerror = () => {
      reject(versionCheck.error);
    };

    versionCheck.onsuccess = () => {
      const currentVersion = versionCheck.result.version;
      versionCheck.result.close();

      // If current version is higher than our target version, delete and recreate
      if (currentVersion > DB_VERSION) {
        console.warn(`Database version ${currentVersion} is higher than expected ${DB_VERSION}. Deleting and recreating...`);
        const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
        
        deleteRequest.onerror = () => {
          reject(deleteRequest.error);
        };

        deleteRequest.onsuccess = () => {
          // Retry initialization after deletion
          initDB().then(resolve).catch(reject);
        };
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = () => {
        reject(request.error);
      };

      request.onsuccess = () => {
        try {
          // Verify all required stores exist
          verifyStoresExist(request.result);
          resolve(request.result);
        } catch (error) {
          // If stores are missing, close the connection and recreate
          request.result.close();
          const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
          
          deleteRequest.onerror = () => {
            reject(deleteRequest.error);
          };

          deleteRequest.onsuccess = () => {
            // Retry initialization after deletion
            initDB().then(resolve).catch(reject);
          };
        }
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create stores if they don't exist
        if (!db.objectStoreNames.contains(TABLES_STORE)) {
          db.createObjectStore(TABLES_STORE);
        }
        if (!db.objectStoreNames.contains(METRICS_STORE)) {
          db.createObjectStore(METRICS_STORE);
        }
        if (!db.objectStoreNames.contains(DATE_STORE)) {
          db.createObjectStore(DATE_STORE);
        }
        if (!db.objectStoreNames.contains(CAMPAIGN_DRAFT_STORE)) {
          db.createObjectStore(CAMPAIGN_DRAFT_STORE);
        }
      };
    };
  });
};

// Modify getData to verify stores before transaction
const getData = async (storeName, key) => {
  try {
    const db = await initDB();
    verifyStoresExist(db);
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onerror = () => {
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve(request.result);
      };
    });
  } catch (error) {
    // If stores are missing or version error, try to reinitialize
    if (error.message.includes('Missing required stores') || error.name === 'VersionError') {
      console.warn('IndexedDB error, attempting to clear and reinitialize:', error);
      try {
        await clearAllStores();
        return getData(storeName, key);
      } catch (retryError) {
        console.error('Failed to reinitialize IndexedDB:', retryError);
        throw retryError;
      }
    }
    throw error;
  }
};

// Set data in store
const setData = async (storeName, key, value) => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(value, key);

      request.onerror = () => {
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve(request.result);
      };
    });
  } catch (error) {
    // If version error, try to reinitialize
    if (error.name === 'VersionError') {
      console.warn('IndexedDB version error, attempting to clear and reinitialize:', error);
      try {
        await clearAllStores();
        return setData(storeName, key, value);
      } catch (retryError) {
        console.error('Failed to reinitialize IndexedDB:', retryError);
        throw retryError;
      }
    }
    throw error;
  }
};

// Delete data from store
const deleteData = async (storeName, key) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(key);

    request.onerror = () => {
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };
  });
};

// Get all data from store
const getAllData = async (storeName) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onerror = () => {
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };
  });
};

// Clear all stores
const clearAllStores = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    try {
      const stores = [TABLES_STORE, METRICS_STORE, DATE_STORE, CAMPAIGN_DRAFT_STORE];
      const transaction = db.transaction(stores, 'readwrite');
      
      stores.forEach(storeName => {
        const store = transaction.objectStore(storeName);
        store.clear();
      });

      transaction.oncomplete = () => {
        resolve();
      };

      transaction.onerror = () => {
        reject(transaction.error);
      };
    } catch (error) {
      reject(error);
    }
  });
};

const tableKey = (key) => `${tableConfigParam()}-${key}`
const metricKey = (key) => `${metricsConfigParam()}-${key}`

export const idb = {
  // Table config operations
  getTableConfig: async (key) => await getData(TABLES_STORE, tableKey(key)),
  setTableConfig: async (key, value) => await setData(TABLES_STORE, tableKey(key), value),
  deleteTableConfig: async (key) => await deleteData(TABLES_STORE, tableKey(key)),
  getAllTableConfigs: async () => await getAllData(TABLES_STORE),
  
  // date operation
  getDateConfig: async (key) => await getData(DATE_STORE, key),
  setDateConfig: async (key, value) => await setData(DATE_STORE, key, value),
  deleteDateConfig: async (key) => await deleteData(DATE_STORE, key),
  
   
  // Metrics config operations  
  getMetricsConfig: async (key) => getData(METRICS_STORE, metricKey(key)),
  setMetricsConfig: async (key, value) => setData(METRICS_STORE, metricKey(key), value),
  deleteMetricsConfig: async (key) => deleteData(METRICS_STORE, metricKey(key)),
  getAllMetricsConfigs: async () => getAllData(METRICS_STORE),

  // Campaign draft operations
  getIdbCampaignDraft: async (key) => getData(CAMPAIGN_DRAFT_STORE, key),
  setIdbCampaignDraft: async (key, value) => setData(CAMPAIGN_DRAFT_STORE, key, value),
  deleteIdbCampaignDraft: async (key) => deleteData(CAMPAIGN_DRAFT_STORE, key),

  // Clear all data
  clearAllData: async () => {
    return new Promise((resolve, reject) => {
      const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
      deleteRequest.onsuccess = () => {
        resolve();
      };
      deleteRequest.onerror = (event) => {
        console.error('Error deleting IndexedDB:', event);
        reject(event);
      };
    });
  },

  // Debug utility to check database status
  getDatabaseInfo: async () => {
    try {
      const db = await initDB();
      return {
        name: db.name,
        version: db.version,
        stores: Array.from(db.objectStoreNames),
        status: 'connected'
      };
    } catch (error) {
      return {
        name: DB_NAME,
        version: DB_VERSION,
        stores: [],
        status: 'error',
        error: error.message
      };
    }
  }
}; 